import React, { useState } from 'react';
import Farmer from '../../components/admin/farmer/Farmer';
import FarmerForm from '../../components/admin/farmer/FarmerForm';
import { useData } from '../../context/DataContext';

const FarmerPage = ({ useApi = false }) => {
  const { farmers, addFarmer, updateFarmer, deleteFarmer } = useData();
  const [showForm, setShowForm] = useState(false);
  const [currentFarmer, setCurrentFarmer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleAddClick = () => {
    setCurrentFarmer(null);
    setShowForm(true);
  };

  const handleEditClick = (farmer) => {
    setCurrentFarmer(farmer);
    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    if (window.confirm('Are you sure you want to delete this farmer?')) {
      deleteFarmer(id);
    }
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);
    try {
      if (currentFarmer) {
        await updateFarmer(currentFarmer.id, formData);
      } else {
        await addFarmer(formData);
      }
      setShowForm(false);
    } catch (err) {
      setError('Failed to save farmer. Please try again.');
      console.error('Error saving farmer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={styles.titleContainer}>
          <span style={styles.titleIcon}>🌾</span>
          <h1 style={styles.title}>
            {useApi ? 'Farmer Database' : 'Farmers Management'}
          </h1>
        </div>
        <p style={styles.subtitle}>
          {useApi
            ? 'View and manage farmer data from the database'
            : 'Register and manage farmer information for better agricultural support'
          }
        </p>
      </div>

      <div style={styles.actionHeader}>
        <div style={styles.stats}>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{farmers.length}</span>
            <span style={styles.statLabel}>Total Farmers</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statValue}>
              {farmers.filter(f => f.district).length}
            </span>
            <span style={styles.statLabel}>Districts</span>
          </div>
        </div>
        {!useApi && !showForm && (
          <button onClick={handleAddClick} style={styles.addButton} className="btn-primary">
            <span style={styles.buttonIcon}>➕</span>
            Add New Farmer
          </button>
        )}
      </div>

      <div style={styles.content}>
        {showForm ? (
          <>
            {error && (
              <div style={styles.errorMessage}>
                {error}
              </div>
            )}
            <FarmerForm
              farmer={currentFarmer}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={isSubmitting}
            />
          </>
        ) : (
          <Farmer
            useApi={useApi}
            localFarmers={farmers}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '1rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '2rem',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  titleIcon: {
    fontSize: '2rem',
    marginRight: '0.75rem',
    color: 'var(--primary-color)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--text-dark)',
    margin: 0,
  },
  subtitle: {
    fontSize: '1.1rem',
    color: 'var(--text-medium)',
    margin: 0,
  },
  actionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  stats: {
    display: 'flex',
    gap: '2rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: 'var(--primary-color)',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: 'var(--text-medium)',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'background-color 0.2s',
  },
  buttonIcon: {
    marginRight: '0.5rem',
  },
  content: {
    marginBottom: '2rem',
  },
  errorMessage: {
    padding: '1rem',
    backgroundColor: 'var(--danger-light)',
    color: 'var(--danger-color)',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontWeight: '500',
  },
};

export default FarmerPage;

